










/**
 * Workflow field visibility: visibilityRules[] where each entry's conditions
 * are ANDed; multiple entries are ORed.
 * Each condition: { sourceUniqueKey, expectedValue } (parent field key + option label).
 */

export function conditionsEqual(a, b) {
  return (
    a.sourceUniqueKey === b.sourceUniqueKey &&
    a.expectedValue === b.expectedValue
  );
}

export function findParentConditionForInputKey(items, inputKey) {
  const stack = Array.isArray(items) ? [...items] : [];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (Array.isArray(node.allInputValues)) {
      const inv = node.allInputValues.find((iv) => iv.inputKey === inputKey);
      if (inv) {
        return {
          sourceUniqueKey: node.uniqueKey,
          expectedValue: inv.inputValue,
        };
      }
    }
    if (node.radioInputValues?.length) {
      stack.push(...node.radioInputValues);
    }
  }
  return null;
}

/** If the field only has legacy radioId, derive an initial rule so multi-parent toggles stay consistent. */
export function visibilityRulesWithLegacySeed(items, item) {
  if (item.visibilityRules?.length) return item.visibilityRules;
  if (!item.radioId) return item.visibilityRules;
  const cond = findParentConditionForInputKey(items, item.radioId);
  if (!cond) return item.visibilityRules;
  return [{ conditions: [cond] }];
}

export function toggleVisibilityCondition(
  visibilityRules,
  ruleIndex,
  parentUniqueKey,
  expectedValue,
  checked
) {
  const rules =
    Array.isArray(visibilityRules) && visibilityRules.length
      ? visibilityRules.map((r) => ({
          conditions: [...(r.conditions || [])],
        }))
      : [{ conditions: [] }];

  while (rules.length <= ruleIndex) {
    rules.push({ conditions: [] });
  }

  let conds = [...rules[ruleIndex].conditions];
  const exists = conds.findIndex((c) =>
    conditionsEqual(c, { sourceUniqueKey: parentUniqueKey, expectedValue })
  );

  if (checked && exists === -1) {
    conds.push({ sourceUniqueKey: parentUniqueKey, expectedValue });
  } else if (!checked && exists !== -1) {
    conds.splice(exists, 1);
  }

  rules[ruleIndex] = { conditions: conds };
  const nonEmpty = rules.filter((r) => (r.conditions || []).length > 0);
  return nonEmpty;
}

export function findItemRecursive(items, uniqueKey) {
  if (!Array.isArray(items)) return null;
  for (const item of items) {
    if (item.uniqueKey === uniqueKey) return item;
    if (item.radioInputValues?.length) {
      const found = findItemRecursive(item.radioInputValues, uniqueKey);
      if (found) return found;
    }
  }
  return null;
}

export function findInputKeyForOption(items, parentUniqueKey, expectedValue) {
  const stack = Array.isArray(items) ? [...items] : [];
  while (stack.length) {
    const item = stack.pop();
    if (!item) continue;
    if (
      item.uniqueKey === parentUniqueKey &&
      Array.isArray(item.allInputValues)
    ) {
      const found = item.allInputValues.find(
        (iv) => iv.inputValue === expectedValue
      );
      if (found) return found.inputKey;
    }
    if (item.radioInputValues?.length) {
      stack.push(...item.radioInputValues);
    }
  }
  return null;
}

export function firstConditionRadioId(items, visibilityRules) {
  if (!visibilityRules?.length) return null;
  const first = visibilityRules[0]?.conditions?.[0];
  if (!first) return null;
  return findInputKeyForOption(
    items,
    first.sourceUniqueKey,
    first.expectedValue
  );
}

export function matchesVisibilityRules(visibilityRules, formData) {
  if (!visibilityRules?.length) return false;
  return visibilityRules.some((rule) => {
    const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
    if (conditions.length === 0) return false;

    // AND across different parent fields, OR within same parent field.
    // This supports cases like a radio/select where multiple options
    // should act as "show if option1 OR option2".
    const bySource = conditions.reduce((acc, c) => {
      if (!c?.sourceUniqueKey) return acc;
      if (!acc[c.sourceUniqueKey]) acc[c.sourceUniqueKey] = [];
      acc[c.sourceUniqueKey].push(c.expectedValue);
      return acc;
    }, {});

    return Object.entries(bySource).every(([sourceUniqueKey, expectedValues]) => {
      const val = formData[sourceUniqueKey];
      const allowed = expectedValues.filter(
        (ev) => ev !== undefined && ev !== null && String(ev) !== ""
      );
      if (allowed.length === 0) return false;

      if (Array.isArray(val)) {
        return allowed.some((ev) => val.includes(ev));
      }
      if (val === undefined || val === null || String(val) === "") return false;
      return allowed.includes(val);
    });
  });
}

export function nestedFieldVisibleByRadioOrRules(
  nestedField,
  formData,
  parentFieldUniqueKey,
  selectedParentOptionInputKey
) {
  if (
    Array.isArray(nestedField.visibilityRules) &&
    nestedField.visibilityRules.length > 0
  ) {
    return matchesVisibilityRules(nestedField.visibilityRules, formData);
  }
  if (nestedField.radioId != null && selectedParentOptionInputKey != null) {
    return nestedField.radioId === selectedParentOptionInputKey;
  }
  return false;
}

export function fieldNameForUniqueKey(items, uniqueKey) {
  const item = findItemRecursive(items, uniqueKey);
  return item?.fieldName || uniqueKey;
}

export function formatVisibilityRulesSummary(items, visibilityRules) {
  if (!visibilityRules?.length) return "";
  return visibilityRules
    .map((rule, ri) => {
      const parts = (rule.conditions || []).map((c) => {
        const label = fieldNameForUniqueKey(items, c.sourceUniqueKey);
        return `${label} = ${c.expectedValue}`;
      });
      const inner = parts.join(" AND ");
      return parts.length ? `Rule ${ri + 1}: ${inner}` : "";
    })
    .filter(Boolean)
    .join(" OR ");
}

export function updateNestedItemByUniqueKey(items, targetUniqueKey, updater) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (item.uniqueKey === targetUniqueKey) {
      return updater(item);
    }
    if (item.radioInputValues?.length) {
      return {
        ...item,
        radioInputValues: updateNestedItemByUniqueKey(
          item.radioInputValues,
          targetUniqueKey,
          updater
        ),
      };
    }
    return item;
  });
}

function mapVisibilityRules(items, mapper) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item || typeof item !== "object") return item;

    const next = { ...item };
    if (Array.isArray(next.visibilityRules)) {
      const visibilityRules = next.visibilityRules
        .map((rule) => ({
          conditions: (rule.conditions || [])
            .map((condition) => mapper(condition))
            .filter(Boolean),
        }))
        .filter((rule) => rule.conditions.length > 0);

      if (visibilityRules.length > 0) {
        next.visibilityRules = visibilityRules;
      } else {
        delete next.visibilityRules;
      }
    }

    if (next.radioInputValues?.length) {
      next.radioInputValues = mapVisibilityRules(next.radioInputValues, mapper);
    }

    return next;
  });
}

export function renameVisibilityRuleExpectedValue(
  items,
  sourceUniqueKey,
  oldExpectedValue,
  newExpectedValue
) {
  if (
    oldExpectedValue === undefined ||
    oldExpectedValue === null ||
    oldExpectedValue === "" ||
    oldExpectedValue === newExpectedValue
  ) {
    return items;
  }
  return mapVisibilityRules(items, (condition) => {
    if (
      condition?.sourceUniqueKey === sourceUniqueKey &&
      condition?.expectedValue === oldExpectedValue
    ) {
      return { ...condition, expectedValue: newExpectedValue };
    }
    return condition;
  });
}

export function removeVisibilityRuleCondition(
  items,
  sourceUniqueKey,
  expectedValue
) {
  return mapVisibilityRules(items, (condition) => {
    if (
      condition?.sourceUniqueKey === sourceUniqueKey &&
      condition?.expectedValue === expectedValue
    ) {
      return null;
    }
    return condition;
  });
}

function sanitizeVisibilityRuleField(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const next = Object.entries(item).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = value.map((child) => sanitizeVisibilityRuleField(child));
    } else {
      acc[key] = sanitizeVisibilityRuleField(value);
    }
    return acc;
  }, {});

  const rules = Array.isArray(next.visibilityRules)
    ? next.visibilityRules.filter((r) => (r.conditions || []).length > 0)
    : [];

  if (rules.length) {
    next.visibilityRules = rules;
    next.radioId = null;
  } else {
    delete next.visibilityRules;
  }

  return next;
}

export function mapFieldsSanitizeVisibilityRules(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => sanitizeVisibilityRuleField(item));
}











